import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getAlley, preloadAlley } from '../alley-assets.js';

// NeonAlley - a Tokyo back street in the rain, from TWO photographs of the
// same alley: its signs off, and its signs on. Synthwave, lo-fi, R&B, city
// pop, trip-hop.
//
// THE DECISIVE DETAIL. Nothing can find a dark sign in a dark photograph.
// But the difference between the lit picture and the unlit one is exactly
// the light each sign throws, and tools/build-alley.mjs cuts that difference
// into lamps: an id map says which sign every pixel receives its light from.
// The whole render is then one line per pixel,
//     colour = off + diff * intensity(sign of the pixel)
// with one intensity per sign, 0 to 1.2, written by the CPU every frame. All
// at 1 and the frame IS alley-on; all at 0 and it is alley-off. Everything
// else in this file is the behaviour of those thirty-odd numbers: neons that
// strike (two or three uneven pulses before they hold), a 100 Hz hum, two or
// three faulty ones that never stop flickering, reflections in the puddles
// that follow their sign and ripple under the rain, and the rain itself.
//
// The photographs are added in LINEAR light: both textures are sRGB and the
// GPU decodes them, diff was encoded from a linear difference by the build,
// so the sum is a sum of light and the equality above is exact (to the webp).

// Shared-rig contract: no rig at all, the picture is its own lighting.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'neonalley',
  family: 'photographic',
  energyLevel: 0.55,
  bpmMin: 70,
  bpmMax: 130,
  movement: 0.45,
  danceability: 0.62,
  tonality: 0.75,
  enabled: true
};

const DEFAULT_TIER = 2;
// Light everywhere: three texture reads a pixel and a 64-entry table. The
// tiers only size the ripple field and count the rain layers.
const TIER = {
  2: { ripple: [192, 64], rainLayers: 2 },
  1: { ripple: [128, 48], rainLayers: 2 },
  0: { ripple: [96, 32], rainLayers: 1 }
};
const SIGNS_W = 64;            // the intensity table, one texel per sign id
const SAFE_FLOOR = 0.5;        // the cover sits at the end of the alley
const MAX_INTENSITY = 1.2;

// --- the neon ------------------------------------------------------------------
// ravesign.js's strike: the discharge hesitates two or three times at uneven
// intervals, each attempt getting further, and only then holds.
const STRIKE_PULSES_MIN = 2, STRIKE_PULSES_MAX = 3;
const STRIKE_PULSE_MS_MIN = 30, STRIKE_PULSE_MS_MAX = 60;
const STRIKE_GAP_MS_MIN = 25, STRIKE_GAP_MS_MAX = 110;
const SETTLE_S = 0.06;         // from the last pulse to full
const HUM_HZ = 100;            // aliased at 60 fps into a shimmer, as a camera sees it
const HUM_DEPTH = 0.03;
// The faulty signs: two or three, flickering for the whole track. bands.high
// shortens the interval.
const FAULTY_MIN = 2, FAULTY_MAX = 3;
const FAIL_ON_S = [0.6, 4.0];
const FAIL_DARK_MS = [50, 380];
// Dormant signs: unlit at the start, one of them strikes on every phrase.
const DORMANT_SHARE = 0.22;
// The beat: the sign under the cursor goes dark and strikes again this many
// beats later; the cursor runs the pattern.
const BLINK_BEATS = 2;
const INTRO_PER_BEAT = 2;      // signs lit per beat while the street opens
const BREATH_GAIN = 1.15, BREATH_S = 0.08;
const DROP_DARK_BEATS = 1;
const BEATS_PER_PHRASE = 16;

const PAT_LTR = 0, PAT_NEAR = 1, PAT_RANDOM = 2;
const PATTERN_COUNT = 3;
// The alley's vanishing point, in image coordinates (y down): near-to-far
// orders the signs by their distance from it.
const VANISHING = [0.52, 0.56];

// --- the rain and the puddles ----------------------------------------------------
// The ripple field covers the lowest PUDDLE_V of the picture (uv.y up).
const PUDDLE_V = 0.30;
const RIPPLE_STRENGTH = 0.010; // uv displacement at unit slope
const RIPPLE_DAMPING = 0.985;
const RIPPLE_SPEED = 0.46;     // wave constant, per step
const DROPS_PER_S = [3, 16];   // bands.mid
const RAIN_SLANT = 0.12;
const TINT_AMOUNT = 0.15;

const SYNTH_BASE = 0.10, SYNTH_DECAY = 1.9;
const SYNTH_PERIOD_LOW = 4.7, SYNTH_PERIOD_MID = 3.1, SYNTH_PERIOD_HIGH = 6.3;
const SYNTH_BPM = 96;

function checkPalette(count) {
  let seed = 12345;
  const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  const out = [];
  for (let id = 0; id <= count; id++) out.push([Math.floor(60 + rnd() * 195), Math.floor(60 + rnd() * 195), Math.floor(60 + rnd() * 195)]);
  return out;
}

// =============================================================================
// SHADERS
// =============================================================================

const VERTEX = `
  varying vec2 vUv;
  varying vec4 vClip;
  void main() {
    vUv = uv;
    vec4 clip = vec4(position.xy, 0.0, 1.0);   // the quad IS the frame
    vClip = clip;
    gl_Position = clip;
  }
`;

// mediump throughout, highp only where the id is decoded: an id read at
// half precision would round two neighbouring signs into one.
const FRAGMENT = `
  precision mediump float;
  uniform sampler2D uOff;
  uniform sampler2D uDiff;
  uniform highp sampler2D uIds;
  uniform sampler2D uSigns;       // 64 x 1: R intensity, GBA the check colour
  uniform sampler2D uRipple;      // R height, GB slope, 0.5 = flat
  uniform float uMean;            // mean intensity, what a far halo follows
  uniform float uReflGain;
  uniform vec3 uTint;
  uniform float uTintAmount;
  uniform float uTime;            // wrapped on the CPU, stays small
  uniform float uRain;            // density 0..1
  uniform float uRainLayers;
  uniform float uRippleOn;
  // uAspect comes with the safe-zone chunk below
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  uniform float uIdDebug;
  uniform float uExact;          // 1: no safe-zone attenuation, the picture as is
  varying vec2 vUv;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}

  // Bounded hash: inputs are fractions in [0, 1) and the products stay
  // small, so it survives the TV's fp16 mediump.
  float hash21(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 1.9);
    return fract(p.x * p.y * 7.31);
  }

  // One layer of rain: slanted columns, a streak falling in each lit column.
  float rainLayer(vec2 uv, float scale, float speed, float seed) {
    vec2 p = vec2((uv.x * uAspect + uv.y * ${RAIN_SLANT.toFixed(2)}) * scale, uv.y * scale * 0.22);
    float col = mod(floor(p.x), 256.0) / 256.0;
    float h = hash21(vec2(col, seed));
    float h2 = hash21(vec2(seed + 0.37, col));
    float on = step(h, uRain);
    float yph = fract(p.y + h2 * 3.0 + uTime * speed);
    float along = step(yph, 0.32) * smoothstep(0.0, 0.06, yph);
    float across = 1.0 - smoothstep(0.0, 0.11, abs(fract(p.x) - 0.5));
    return on * along * across;
  }

  void main() {
    vec2 uv = vUv * uUvScale + uUvOffset;        // cover fit
    highp vec4 idc = texture2D(uIds, uv);
    highp float id = floor(idc.r * 255.0 + 0.5) * 256.0 + floor(idc.g * 255.0 + 0.5);
    float w = idc.b;
    float refl = idc.a;
    vec4 sd = texture2D(uSigns, vec2((id + 0.5) / ${SIGNS_W}.0, 0.5));

    if (uIdDebug > 0.5) {
      // the build's check picture: sign colour, halos dimmed by weight,
      // reflections tinted towards white
      float k = w > 0.999 ? 1.0 : 0.15 + 0.45 * w;
      vec3 c = sd.gba * k + vec3(refl * 60.0 / 255.0);
      // The output pass encodes to sRGB; the check picture's values are
      // already sRGB, so hand them over decoded and they come out equal.
      vec3 lo = c / 12.92;
      vec3 hi = pow((c + 0.055) / 1.055, vec3(2.4));
      c = mix(lo, hi, step(0.04045, c));
      gl_FragColor = vec4(c, 1.0);
      return;
    }

    float I = mix(uMean, sd.r, w);
    I *= mix(1.0, uReflGain, refl);

    // The puddles: the ripple field displaces where the reflection is read,
    // and its slopes catch the reflected light as rings.
    vec2 duv = vec2(0.0);
    float rings = 0.0;
    if (uRippleOn > 0.5 && uv.y < ${PUDDLE_V.toFixed(2)}) {
      float band = smoothstep(${PUDDLE_V.toFixed(2)}, ${(PUDDLE_V - 0.10).toFixed(2)}, uv.y);
      float puddle = band * (0.45 + 0.55 * refl);
      vec4 rp = texture2D(uRipple, vec2(uv.x, uv.y / ${PUDDLE_V.toFixed(2)}));
      vec2 g = (rp.gb - 0.5) * 2.0;
      duv = g * ${RIPPLE_STRENGTH.toFixed(4)} * puddle;
      rings = (abs(g.x) + abs(g.y)) * puddle;
    }

    vec3 off = texture2D(uOff, uv).rgb;
    vec3 diff = texture2D(uDiff, uv + duv).rgb;
    diff *= mix(vec3(1.0), uTint, uTintAmount);
    vec3 light = diff * I;
    vec3 col = off + light + light * rings * 1.6;

    // The rain, lit by what it falls in front of.
    if (uRainLayers > 0.5) {
      float lum = dot(light, vec3(0.2126, 0.7152, 0.0722));
      float r = rainLayer(uv, 150.0, 0.9, 0.21);
      if (uRainLayers > 1.5) r += 0.6 * rainLayer(uv, 260.0, 0.6, 0.63);
      col += r * (0.010 + 0.45 * lum);
    }

    float att = safeZoneAttenuation(vClip);
    att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((att - 0.32) / 0.68, 0.0, 1.0));
    att = mix(att, 1.0, uExact);
    gl_FragColor = vec4(col * att, 1.0);
  }
`;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
function rand(a, b) { return a + Math.random() * (b - a); }
function lerp(a, b, t) { return a + (b - a) * t; }

export class NeonAlleyVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    // ?alleyid=1 draws the build's check picture (a colour per sign).
    this._idDebug = urlParam('alleyid') === '1';
    // ?alleyall=<f> pins every sign at one intensity: 1 is alley-on, 0 is
    // alley-off. ?alleysign=<n> lights sign n alone, with its reflection.
    const all = urlParam('alleyall');
    this._allDebug = all != null && all !== '' && Number.isFinite(parseFloat(all)) ? parseFloat(all) : null;
    const one = urlParam('alleysign');
    this._oneDebug = one != null && one !== '' && Number.isFinite(parseInt(one, 10)) ? parseInt(one, 10) : null;
    // ?alleyprobe=1 publishes the sign states on window.__neonalley.
    this._probe = urlParam('alleyprobe') === '1';
    // The exactness checks look at the shader's own output: no bloom, no
    // tone mapping, no grain, like rosace.js's id view.
    const exact = this._idDebug || this._allDebug != null || this._oneDebug != null;
    this._exact = exact;
    this._postProcessing = exact
      ? { bloomStrength: 0, vignette: 0, grain: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }
      : { bloomStrength: 0.45, vignette: 0.30, aberration: 0.0012 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const assets = getAlley();
    this._assets = assets;
    // A forced or early start can run before the preload has landed; build
    // the quad now and rebuild the whole piece once the assets arrive.
    if (!assets) {
      const self = this;
      preloadAlley().then(function (a) {
        if (!a || !self._scene || self._assets) return;
        const s = self._scene, c = self._camera;
        self.dispose();
        self.init(s, c);
      });
    }
    const data = assets ? assets.data : { count: 0, size: [1376, 768], signs: [] };
    this._count = data.count;
    this._imageAspect = data.size[0] / data.size[1];
    this._signs = [];
    for (const s of data.signs) {
      this._signs.push({
        id: s.id, cx: s.cx, cy: s.cy, area: s.area,
        state: 'off', pulses: null, t0: 0, dur: 0, lit: 0,
        faulty: false, dormant: false, offUntil: -1, failNextAt: 0,
        humPhase: Math.random() * Math.PI * 2
      });
    }
    this._bySign = new Array(this._count + 1).fill(null);
    for (const S of this._signs) this._bySign[S.id] = S;

    // --- the intensity table
    this._signsData = new Float32Array(SIGNS_W * 4);
    const pal = checkPalette(this._count);
    for (let i = 0; i < SIGNS_W; i++) {
      const c = pal[i] || [0, 0, 0];
      this._signsData[i * 4] = 0;
      this._signsData[i * 4 + 1] = c[0] / 255;
      this._signsData[i * 4 + 2] = c[1] / 255;
      this._signsData[i * 4 + 3] = c[2] / 255;
    }
    this._signsTex = new THREE.DataTexture(this._signsData, SIGNS_W, 1, THREE.RGBAFormat, THREE.FloatType);
    this._signsTex.minFilter = THREE.NearestFilter;
    this._signsTex.magFilter = THREE.NearestFilter;
    this._signsTex.generateMipmaps = false;
    this._signsTex.needsUpdate = true;

    // --- the ripple field
    const [gw, gh] = this._layout.ripple;
    this._gw = gw; this._gh = gh;
    this._h0 = new Float32Array(gw * gh);
    this._h1 = new Float32Array(gw * gh);
    this._rippleData = new Uint8Array(gw * gh * 4);
    for (let i = 0; i < gw * gh; i++) { this._rippleData[i * 4] = 128; this._rippleData[i * 4 + 1] = 128; this._rippleData[i * 4 + 2] = 128; this._rippleData[i * 4 + 3] = 255; }
    this._rippleTex = new THREE.DataTexture(this._rippleData, gw, gh, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._rippleTex.minFilter = THREE.LinearFilter;
    this._rippleTex.magFilter = THREE.LinearFilter;
    this._rippleTex.generateMipmaps = false;
    this._rippleTex.needsUpdate = true;
    this._dropCarry = 0;
    this._drops = 0;

    this._uniforms = Object.assign({
      uOff: { value: assets ? assets.off : null },
      uDiff: { value: assets ? assets.diff : null },
      uIds: { value: assets ? assets.ids : null },
      uSigns: { value: this._signsTex },
      uRipple: { value: this._rippleTex },
      uMean: { value: 0 },
      uReflGain: { value: 1 },
      uTint: { value: new THREE.Vector3(1, 1, 1) },
      uTintAmount: { value: this._exact ? 0 : TINT_AMOUNT },
      uTime: { value: 0 },
      uRain: { value: 0.6 },
      uRainLayers: { value: this._exact ? 0 : this._layout.rainLayers },
      uRippleOn: { value: this._exact ? 0 : 1 },
      uAspect: { value: 16 / 9 },
      uUvScale: { value: new THREE.Vector2(1, 1) },
      uUvOffset: { value: new THREE.Vector2(0, 0) },
      uIdDebug: { value: this._idDebug ? 1 : 0 },
      uExact: { value: this._exact ? 1 : 0 }
    }, safeZoneUniforms);
    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms, vertexShader: VERTEX, fragmentShader: FRAGMENT,
      depthTest: false, depthWrite: false
    });
    this._geometry = new THREE.PlaneGeometry(2, 2);
    this._quad = new THREE.Mesh(this._geometry, this._material);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -10;
    scene.add(this._quad);

    // --- the behaviour
    this._time = 0;
    this._step = 0;
    this._sinceStep = 0;
    this._beatPeriod = 60 / SYNTH_BPM;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrasePhase = null;
    this._lastMode = null;
    this._breathUntil = -1;
    this._dropRelightAt = -1;
    this._dropDark = false;
    this._pattern = PAT_LTR;
    this._cursor = 0;
    this._order = [];
    this._introQueue = [];
    this._bands = { low: 0.5, mid: 0.5, high: 0.5 };
    this._synth = { low: SYNTH_BASE, mid: SYNTH_BASE, high: SYNTH_BASE };
    this._synthPhase = { low: null, mid: null, high: null };
    this._lastPaletteRef = undefined;
    this._scratch = new THREE.Color();
    this._aspect = 0;
    this._castSigns();
    this._applyPalette(null);
    this._writeSigns(0);
    this._reframe();
  }

  // Who is faulty, who is dormant, and the order the street opens in.
  _castSigns() {
    const signs = this._signs;
    if (!signs.length) return;
    // Faulty: among the middle-sized lamps (a flickering shop window reads
    // as a broken render, a flickering lantern as a broken lantern).
    const mid = signs.filter((S) => S.area >= 400 && S.area <= 15000);
    const pool = (mid.length >= FAULTY_MAX ? mid : signs).slice();
    const nf = Math.min(pool.length, FAULTY_MIN + Math.floor(Math.random() * (FAULTY_MAX - FAULTY_MIN + 1)));
    for (let i = 0; i < nf; i++) {
      const k = Math.floor(Math.random() * pool.length);
      pool[k].faulty = true;
      pool[k].failNextAt = rand(0.5, 3);
      pool.splice(k, 1);
    }
    // Dormant: unlit until a phrase wakes them.
    const rest = signs.filter((S) => !S.faulty);
    const nd = Math.max(2, Math.round(rest.length * DORMANT_SHARE));
    for (let i = 0; i < nd && rest.length; i++) {
      const k = Math.floor(Math.random() * rest.length);
      rest[k].dormant = true;
      rest.splice(k, 1);
    }
    // The intro: the street opens left to right, one lamp after another.
    this._introQueue = signs.filter((S) => !S.dormant).sort((a, b) => a.cx - b.cx);
    this._setPattern(PAT_LTR);
    // The debug views skip the intro and the cast: the table is pinned.
    if (this._exact) {
      this._introQueue = [];
      for (const S of signs) { S.faulty = false; S.dormant = false; S.state = 'on'; S.lit = 1; }
    }
  }

  _setPattern(p) {
    this._pattern = p;
    const signs = this._signs.slice();
    if (p === PAT_LTR) signs.sort((a, b) => a.cx - b.cx);
    else if (p === PAT_NEAR) {
      const d = (S) => (S.cx - VANISHING[0]) ** 2 + ((S.cy - VANISHING[1]) * 0.56) ** 2;
      signs.sort((a, b) => d(b) - d(a));
    } else {
      for (let i = signs.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); const t = signs[i]; signs[i] = signs[j]; signs[j] = t; }
    }
    this._order = signs;
    this._cursor = 0;
  }

  // --- the strike ------------------------------------------------------------
  _beginStrike(S) {
    const n = STRIKE_PULSES_MIN + Math.floor(Math.random() * (STRIKE_PULSES_MAX - STRIKE_PULSES_MIN + 1));
    const pulses = [];
    let t = 0;
    for (let i = 0; i < n; i++) {
      t += rand(STRIKE_GAP_MS_MIN, STRIKE_GAP_MS_MAX) / 1000;
      const d = rand(STRIKE_PULSE_MS_MIN, STRIKE_PULSE_MS_MAX) / 1000;
      pulses.push([t, t + d, lerp(0.30, 0.95, (i + 1) / n)]);
      t += d;
    }
    S.pulses = pulses;
    S.state = 'striking';
    S.t0 = this._time;
    S.dur = t;                 // the last pulse does not go out: it holds
    S.dormant = false;
  }

  _turnOff(S) {
    S.state = 'off';
    S.pulses = null;
    S.lit = 0;
  }

  _stepSign(S, dt) {
    if (S.state === 'striking') {
      const t = this._time - S.t0;
      let v = 0.03;
      for (const p of S.pulses) if (t >= p[0] && t < p[1]) v = p[2];
      S.lit = v;
      if (t >= S.dur) { S.state = 'settling'; S.t0 = this._time; S.lit = 0.95; }
    } else if (S.state === 'settling') {
      const t = (this._time - S.t0) / SETTLE_S;
      S.lit = lerp(0.95, 1, clamp01(t));
      if (t >= 1) S.state = 'on';
    } else if (S.state === 'on') {
      S.lit = 1;
    } else {
      S.lit = 0;
    }
    void dt;
  }

  // --- the rhythm -----------------------------------------------------------------
  _onBeat() {
    this._step++;
    this._sinceStep = 0;
    if (this._exact) return;
    // The street opening: a couple of lamps per beat, no blinking yet.
    if (this._introQueue.length) {
      for (let i = 0; i < INTRO_PER_BEAT && this._introQueue.length; i++) {
        const S = this._introQueue.shift();
        if (S.state === 'off') this._beginStrike(S);
      }
      return;
    }
    if (this._dropDark) return;
    // The cursor's sign: lit, it goes dark and strikes again in two beats;
    // dark (and not dormant), it strikes now.
    if (!this._order.length) return;
    let S = null;
    for (let k = 0; k < this._order.length; k++) {
      const c = this._order[(this._cursor + k) % this._order.length];
      if (c.faulty || c.dormant) continue;   // woken by the phrase, not the beat
      S = c; this._cursor = (this._cursor + k + 1) % this._order.length;
      break;
    }
    if (!S) return;
    if (S.state !== 'off') {
      this._turnOff(S);
      S.offUntil = this._time + BLINK_BEATS * this._beatPeriod;
    } else if (!S.dormant) {
      this._beginStrike(S);
      S.offUntil = -1;
    }
  }

  _onBar() {
    if (this._exact) return;
    this._breathUntil = this._time + BREATH_S;
  }

  _onPhrase() {
    if (this._exact) return;
    this._setPattern((this._pattern + 1 + Math.floor(Math.random() * (PATTERN_COUNT - 1))) % PATTERN_COUNT);
    // A sign that has been dark for long strikes: a dormant one first, else
    // the one that has waited longest.
    const dormant = this._signs.filter((S) => S.dormant);
    if (dormant.length) {
      this._beginStrike(dormant[Math.floor(Math.random() * dormant.length)]);
    } else {
      let best = null;
      for (const S of this._signs) if (S.state === 'off' && !S.faulty && (!best || S.offUntil < best.offUntil)) best = S;
      if (best) this._beginStrike(best);
    }
  }

  _onDrop() {
    if (this._exact) return;
    // Black for a beat: everything but the faulty ones, which keep failing.
    for (const S of this._signs) if (!S.faulty) { this._turnOff(S); S.offUntil = -1; }
    this._dropDark = true;
    this._dropRelightAt = this._time + DROP_DARK_BEATS * this._beatPeriod;
    this._introQueue = [];
  }

  _stepSynthBands(deltaTime, profile) {
    const k = Math.exp(-SYNTH_DECAY * deltaTime);
    const sy = this._synth;
    sy.low = SYNTH_BASE + (sy.low - SYNTH_BASE) * k;
    sy.mid = SYNTH_BASE + (sy.mid - SYNTH_BASE) * k;
    sy.high = SYNTH_BASE + (sy.high - SYNTH_BASE) * k;
    const fired = (key, phase) => {
      const prev = this._synthPhase[key];
      this._synthPhase[key] = phase;
      return prev != null && phase < prev;
    };
    const bar = profile.barPhase, beat = profile.beatPhase;
    if (bar != null && beat != null) {
      if (fired('low', bar)) sy.low = 1.0;
      if (fired('mid', (bar + 0.5) % 1)) sy.mid = 0.85;
      if (fired('high', (beat + 0.5) % 1)) sy.high = 0.75;
      return;
    }
    const t = this._time;
    if (fired('low', (t / SYNTH_PERIOD_LOW) % 1)) sy.low = 1.0;
    if (fired('mid', (t / SYNTH_PERIOD_MID) % 1)) sy.mid = 0.85;
    if (fired('high', (t / SYNTH_PERIOD_HIGH) % 1)) sy.high = 0.75;
  }

  _effectiveBands(director) {
    if (director && director.hasReliableGrid && director.bands) return director.bands;
    return this._synth;
  }

  // --- the palette: a 15% tint on the light, never a repaint ------------------------
  _applyPalette(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : null;
    const t = this._uniforms.uTint.value;
    if (!vivid) { t.set(1, 1, 1); return; }
    this._scratch.set(vivid[0]);
    const c = this._scratch;
    const mx = Math.max(c.r, c.g, c.b) || 1;
    // Normalised to a unit peak so the tint changes the hue, not the exposure.
    t.set(c.r / mx, c.g / mx, c.b / mx);
  }

  // --- the frame: cover fit of the picture, driven by the live aspect -----------------
  _reframe() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    this._aspect = aspect;
    const u = this._uniforms;
    u.uAspect.value = aspect;
    if (aspect > this._imageAspect) {
      // wider than the picture: crop top and bottom
      const k = this._imageAspect / aspect;
      u.uUvScale.value.set(1, k);
      u.uUvOffset.value.set(0, (1 - k) / 2);
    } else {
      const k = aspect / this._imageAspect;
      u.uUvScale.value.set(k, 1);
      u.uUvOffset.value.set((1 - k) / 2, 0);
    }
  }

  // --- the puddles: a wave field at low resolution, a drop at every raindrop ------------
  _stepRipple(dt, rainAmount) {
    const gw = this._gw, gh = this._gh;
    const h0 = this._h0, h1 = this._h1;
    // drops: Poisson at a rate set by the rain
    const rate = lerp(DROPS_PER_S[0], DROPS_PER_S[1], rainAmount);
    this._dropCarry += rate * dt;
    while (this._dropCarry >= 1) {
      this._dropCarry -= 1;
      const x = 1 + Math.floor(Math.random() * (gw - 2)), y = 1 + Math.floor(Math.random() * (gh - 2));
      const a = rand(0.5, 1.0);
      h1[y * gw + x] -= a;
      h1[y * gw + x - 1] -= a * 0.4; h1[y * gw + x + 1] -= a * 0.4;
      h1[(y - 1) * gw + x] -= a * 0.4; h1[(y + 1) * gw + x] -= a * 0.4;
      this._drops++;
    }
    // the wave: h_new = (2 h - h_old) + c (laplacian h), damped
    const c = RIPPLE_SPEED;
    for (let y = 1; y < gh - 1; y++) {
      for (let x = 1; x < gw - 1; x++) {
        const i = y * gw + x;
        const lap = h1[i - 1] + h1[i + 1] + h1[i - gw] + h1[i + gw] - 4 * h1[i];
        h0[i] = (2 * h1[i] - h0[i] + c * lap) * RIPPLE_DAMPING;
      }
    }
    // swap: h0 now holds the newest field
    this._h0 = h1; this._h1 = h0;
    const h = h0;
    const d = this._rippleData;
    for (let y = 0; y < gh; y++) {
      for (let x = 0; x < gw; x++) {
        const i = y * gw + x;
        const gx = (x > 0 && x < gw - 1) ? (h[i + 1] - h[i - 1]) * 0.5 : 0;
        const gy = (y > 0 && y < gh - 1) ? (h[i + gw] - h[i - gw]) * 0.5 : 0;
        d[i * 4] = Math.max(0, Math.min(255, Math.round(128 + h[i] * 100)));
        d[i * 4 + 1] = Math.max(0, Math.min(255, Math.round(128 + gx * 400)));
        d[i * 4 + 2] = Math.max(0, Math.min(255, Math.round(128 + gy * 400)));
      }
    }
    this._rippleTex.needsUpdate = true;
  }

  // --- the table ------------------------------------------------------------------
  _writeSigns(dt) {
    const data = this._signsData;
    const breath = this._time < this._breathUntil ? BREATH_GAIN : 1;
    let sum = 0, n = 0;
    for (const S of this._signs) {
      let v;
      if (this._allDebug != null) v = this._allDebug;
      else if (this._oneDebug != null) v = S.id === this._oneDebug ? 1 : 0;
      else {
        this._stepSign(S, dt);
        S.humPhase = (S.humPhase + dt * HUM_HZ * Math.PI * 2) % (Math.PI * 2);
        const hum = 1 + HUM_DEPTH * Math.sin(S.humPhase);
        v = S.lit * hum * breath;
      }
      v = Math.max(0, Math.min(MAX_INTENSITY, v));
      data[S.id * 4] = v;
      sum += v; n++;
    }
    data[0] = 0;
    this._signsTex.needsUpdate = true;
    // The far halos follow the alley's mean: in the single-sign view they
    // follow nothing, so that one sign and its reflection are all there is.
    this._uniforms.uMean.value = this._oneDebug != null ? 0 : (n ? sum / n : 0);
  }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return;   // gel
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    this._sinceStep += dt;
    const director = profile.director;
    const u = this._uniforms;

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    this._stepSynthBands(dt, profile);
    const bands = this._effectiveBands(director);
    const low = bands && bands.low != null ? bands.low : 0.5;
    const mid = bands && bands.mid != null ? bands.mid : 0.5;
    const high = bands && bands.high != null ? bands.high : 0.5;
    // smoothed, so the rain and the reflections do not flicker with the envelope
    const k = Math.min(1, dt / 0.6);
    this._bands.low += (low - this._bands.low) * k;
    this._bands.mid += (mid - this._bands.mid) * k;
    this._bands.high += (high - this._bands.high) * k;

    // --- the beat, the bar, the phrase, the drop
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) this._onBeat();
      this._lastBeatPhase = beatPhase;
    } else if (this._sinceStep >= this._beatPeriod) {
      this._onBeat();
    }
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) {
      if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar();
      this._lastBarPhase = barPhase;
    } else if (this._sinceStep === 0 && this._step % 4 === 0) {
      this._onBar();
    }
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) {
      if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase();
      this._lastPhrasePhase = phrasePhase;
    } else if (this._sinceStep === 0 && this._step > 0 && this._step % BEATS_PER_PHRASE === 0) {
      this._onPhrase();
    }
    const mode = director ? director.mode : null;
    if (mode === 'drop' && this._lastMode !== 'drop' && !this._dropDark) this._onDrop();
    this._lastMode = mode;
    if (this._dropDark && this._time >= this._dropRelightAt) {
      // then EVERYTHING strikes at once, each with its own hesitation
      this._dropDark = false;
      for (const S of this._signs) if (!S.faulty) this._beginStrike(S);
    }

    // --- the blinked signs come back, the faulty ones keep failing
    if (!this._exact) {
      for (const S of this._signs) {
        if (S.state === 'off' && S.offUntil >= 0 && this._time >= S.offUntil && !this._dropDark) {
          S.offUntil = -1;
          this._beginStrike(S);
        }
        if (S.faulty && this._time >= S.failNextAt) {
          if (S.state !== 'off') {
            this._turnOff(S);
            S.failNextAt = this._time + rand(FAIL_DARK_MS[0], FAIL_DARK_MS[1]) / 1000;
          } else {
            this._beginStrike(S);
            // bands.high: more failures when the top end is busy
            const scale = 1.35 - 0.7 * this._bands.high;
            S.failNextAt = this._time + rand(FAIL_ON_S[0], FAIL_ON_S[1]) * scale;
          }
        }
      }
    }

    this._writeSigns(dt);
    u.uReflGain.value = 0.7 + 0.6 * this._bands.low;
    u.uRain.value = 0.35 + 0.55 * this._bands.mid;
    if (!this._exact) this._stepRipple(dt, this._bands.mid);
    u.uTime.value = this._time % 16;

    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && aspect !== this._aspect) this._reframe();

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }
    if (this._probe && typeof window !== 'undefined') window.__neonalley = this.getProbe();
  }

  getProbe() {
    const out = [];
    for (const S of this._signs) out.push({ id: S.id, state: S.state, lit: +S.lit.toFixed(3), v: +this._signsData[S.id * 4].toFixed(3), faulty: S.faulty, dormant: S.dormant });
    return {
      t: this._time, step: this._step, pattern: this._pattern, intro: this._introQueue.length,
      dropDark: this._dropDark, mean: this._uniforms.uMean.value, drops: this._drops, signs: out
    };
  }

  dispose() {
    if (this._quad) this._scene.remove(this._quad);
    if (this._geometry) this._geometry.dispose();
    if (this._material) this._material.dispose();
    if (this._signsTex) this._signsTex.dispose();
    if (this._rippleTex) this._rippleTex.dispose();
    this._quad = null;
    if (typeof window !== 'undefined' && window.__neonalley) delete window.__neonalley;
  }
}
